import { Check, ChevronDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';

import { FieldControlContext, type FieldControlState, useFieldControl } from './form-field';
import { Input } from './input';
import { popoverSurfaceClassName } from './surface';

export interface SelectOption {
    value: string;
    label: React.ReactNode;
    disabled?: boolean;
    /** Shown after the label (e.g. link/icon); must call `stopPropagation` so the row is not selected as a value. */
    trailing?: React.ReactNode;
    /** When `searchable` is true, used for substring matching (defaults to string `label`, then `value`). */
    searchText?: string;
}

function optionFilterText(option: SelectOption): string {
    if (option.searchText !== undefined) return option.searchText;
    if (typeof option.label === 'string') return option.label;
    return option.value;
}

function optionMatchesFilter(option: SelectOption, query: string): boolean {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return true;
    return optionFilterText(option).toLowerCase().includes(trimmed);
}

export const selectTriggerClassName =
    'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-admin-field-border admin-field-fill px-3 text-left text-sm font-medium text-neutral-950 shadow-none transition-colors hover:bg-[var(--admin-field-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 aria-[invalid=true]:border-destructive disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-600';

export interface SelectProps {
    ariaLabel: string;
    options: SelectOption[];
    value: string;
    onValueChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    /** Adds a filter field above the list (client-side match on `searchText` / label / value). */
    searchable?: boolean;
    searchPlaceholder?: string;
    /** Accessible name for the search field (defaults to `${ariaLabel} search`). */
    searchAriaLabel?: string;
    onBlur?: () => void;
}

type SelectKeyEvent = React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>;

interface SelectKeyboardState {
    activeIndex: number;
    enabledIndexes: number[];
    filteredOptions: SelectOption[];
    filterQuery: string;
    open: boolean;
    trigger: HTMLButtonElement | null;
    value: string;
    choose: (option: SelectOption) => void;
    setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setOpeningActiveIndex: (index: number) => void;
}

function getOpeningActiveIndex(key: 'ArrowDown' | 'ArrowUp', state: SelectKeyboardState): number {
    const selectedIndex = state.filteredOptions.findIndex(
        (option) => option.value === state.value && !option.disabled,
    );
    if (selectedIndex >= 0) return selectedIndex;
    return key === 'ArrowUp'
        ? (state.enabledIndexes.at(-1) ?? -1)
        : (state.enabledIndexes[0] ?? -1);
}

function getMovedActiveIndex(key: 'ArrowDown' | 'ArrowUp', state: SelectKeyboardState): number {
    const currentPosition = state.enabledIndexes.indexOf(state.activeIndex);
    const nextPosition =
        key === 'ArrowDown'
            ? Math.min(
                  currentPosition < 0 ? 0 : currentPosition + 1,
                  state.enabledIndexes.length - 1,
              )
            : Math.max(
                  currentPosition < 0 ? state.enabledIndexes.length - 1 : currentPosition - 1,
                  0,
              );
    return state.enabledIndexes[nextPosition] ?? -1;
}

function handleSelectArrowKey(
    event: SelectKeyEvent,
    key: 'ArrowDown' | 'ArrowUp',
    state: SelectKeyboardState,
) {
    event.preventDefault();
    if (!state.open) {
        state.setOpeningActiveIndex(getOpeningActiveIndex(key, state));
        state.setOpen(true);
        return;
    }
    state.setActiveIndex(getMovedActiveIndex(key, state));
}

function handleSelectBoundaryKey(
    event: SelectKeyEvent,
    key: 'Home' | 'End',
    fromTrigger: boolean,
    state: SelectKeyboardState,
) {
    if (!state.open || (!fromTrigger && state.filterQuery !== '')) return;
    event.preventDefault();
    state.setActiveIndex(
        key === 'Home' ? (state.enabledIndexes[0] ?? -1) : (state.enabledIndexes.at(-1) ?? -1),
    );
}

function handleSelectCommitKey(
    event: SelectKeyEvent,
    fromTrigger: boolean,
    state: SelectKeyboardState,
) {
    event.preventDefault();
    event.stopPropagation();
    const option = state.open ? state.filteredOptions[state.activeIndex] : undefined;
    if (option) {
        state.choose(option);
    } else if (!state.open && fromTrigger) {
        state.setOpen(true);
    }
}

function handleSelectKey(event: SelectKeyEvent, state: SelectKeyboardState) {
    const fromTrigger = event.currentTarget === state.trigger;
    switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
            handleSelectArrowKey(event, event.key, state);
            return;
        case 'Home':
        case 'End':
            handleSelectBoundaryKey(event, event.key, fromTrigger, state);
            return;
        case 'Enter':
            handleSelectCommitKey(event, fromTrigger, state);
            return;
        case ' ':
            if (fromTrigger) handleSelectCommitKey(event, fromTrigger, state);
            return;
        case 'Tab':
            state.setOpen(false);
    }
}

function preventSpaceKeyUp(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.key === ' ') event.preventDefault();
}

function getTriggerActiveDescendant(
    searchable: boolean,
    open: boolean,
    activeIndex: number,
    optionId: (index: number) => string,
): string | undefined {
    if (searchable || !open || activeIndex < 0) return undefined;
    return optionId(activeIndex);
}

function getFieldInvalid(field: FieldControlState | null): true | undefined {
    return field?.invalid ? true : undefined;
}

function getFieldRequired(field: FieldControlState | null, disabled: boolean): true | undefined {
    return field?.required && !disabled ? true : undefined;
}

function getTriggerKeyDown(
    searchable: boolean,
    handler: (event: SelectKeyEvent) => void,
): ((event: React.KeyboardEvent<HTMLButtonElement>) => void) | undefined {
    return searchable ? undefined : handler;
}

function getTriggerKeyUp(
    searchable: boolean,
): React.KeyboardEventHandler<HTMLButtonElement> | undefined {
    return searchable ? undefined : preventSpaceKeyUp;
}

function getSelectedOptionTitle(option?: SelectOption): string | undefined {
    return typeof option?.label === 'string' ? option.label : undefined;
}

function getSelectedOptionLabel(option: SelectOption | undefined, placeholder: string) {
    return option?.label ?? placeholder;
}

function SelectTrigger({
    activeIndex,
    ariaLabel,
    className,
    disabled,
    field,
    listboxId,
    onBlur,
    onKeyDown,
    open,
    optionId,
    placeholder,
    searchable,
    selectedOption,
    setOpen,
    triggerRef,
}: {
    activeIndex: number;
    ariaLabel: string;
    className?: string;
    disabled: boolean;
    field: FieldControlState | null;
    listboxId: string;
    onBlur?: () => void;
    onKeyDown: (event: SelectKeyEvent) => void;
    open: boolean;
    optionId: (index: number) => string;
    placeholder: string;
    searchable: boolean;
    selectedOption?: SelectOption;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    triggerRef: React.RefObject<HTMLButtonElement>;
}) {
    return (
        <button
            ref={triggerRef}
            type="button"
            role="combobox"
            aria-label={ariaLabel}
            aria-controls={listboxId}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-activedescendant={getTriggerActiveDescendant(
                searchable,
                open,
                activeIndex,
                optionId,
            )}
            aria-describedby={field?.describedBy}
            aria-invalid={getFieldInvalid(field)}
            aria-required={getFieldRequired(field, disabled)}
            disabled={disabled}
            className={cn(
                selectTriggerClassName,
                'dark:text-neutral-100 dark:disabled:text-neutral-400',
                className,
            )}
            onClick={() => setOpen((current) => !current)}
            onKeyDown={getTriggerKeyDown(searchable, onKeyDown)}
            onKeyUp={getTriggerKeyUp(searchable)}
            onBlur={onBlur}
        >
            <span
                className={cn('min-w-0 truncate', { 'text-neutral-500': !selectedOption })}
                title={getSelectedOptionTitle(selectedOption)}
            >
                {getSelectedOptionLabel(selectedOption, placeholder)}
            </span>
            <ChevronDown
                aria-hidden="true"
                className={cn('size-4 shrink-0 text-neutral-500 transition-transform', {
                    'rotate-180': open,
                })}
            />
        </button>
    );
}

function SelectOptionRow({
    active,
    index,
    onChoose,
    onPoint,
    option,
    optionId,
    selected,
}: {
    active: boolean;
    index: number;
    onChoose: (option: SelectOption) => void;
    onPoint: (index: number) => void;
    option: SelectOption;
    optionId: (index: number) => string;
    selected: boolean;
}) {
    return (
        <div
            className={cn(
                'flex w-full items-stretch text-neutral-950 transition-colors dark:text-neutral-50',
                (selected || active) && 'bg-neutral-100 dark:bg-neutral-800',
                selected && 'font-semibold',
            )}
        >
            <button
                id={optionId(index)}
                type="button"
                role="option"
                tabIndex={-1}
                aria-selected={selected}
                disabled={option.disabled}
                className={cn(
                    'flex min-w-0 flex-1 items-start justify-between gap-2 px-3 py-2 text-left',
                    'disabled:cursor-not-allowed disabled:text-neutral-400',
                )}
                onClick={() => onChoose(option)}
                onPointerMove={() => onPoint(index)}
            >
                <span className="min-w-0 whitespace-normal break-words">{option.label}</span>
                {selected ? (
                    <Check
                        aria-hidden="true"
                        className="mt-0.5 size-4 shrink-0 text-neutral-700 dark:text-neutral-200"
                    />
                ) : null}
            </button>
            {option.trailing ? (
                <div className="flex shrink-0 items-center border-l border-neutral-200 dark:border-neutral-700">
                    {option.trailing}
                </div>
            ) : null}
        </div>
    );
}

function SelectPopup({
    activeIndex,
    ariaLabel,
    filterQuery,
    filteredOptions,
    listboxId,
    onChoose,
    onFilterChange,
    onKeyDown,
    optionId,
    searchAriaLabel,
    searchInputRef,
    searchPlaceholder,
    searchable,
    setActiveIndex,
    value,
}: {
    activeIndex: number;
    ariaLabel: string;
    filterQuery: string;
    filteredOptions: SelectOption[];
    listboxId: string;
    onChoose: (option: SelectOption) => void;
    onFilterChange: (value: string) => void;
    onKeyDown: (event: SelectKeyEvent) => void;
    optionId: (index: number) => string;
    searchAriaLabel?: string;
    searchInputRef: React.RefObject<HTMLInputElement>;
    searchPlaceholder: string;
    searchable: boolean;
    setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
    value: string;
}) {
    return (
        <div
            className={cn(
                popoverSurfaceClassName,
                'absolute z-50 mt-1 max-h-72 w-full overflow-hidden text-sm font-medium text-neutral-950 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-50',
            )}
        >
            <FieldControlContext.Provider value={null}>
                {searchable ? (
                    <div
                        className="border-b border-neutral-200 p-2 dark:border-neutral-700"
                        onPointerDown={(event) => event.stopPropagation()}
                    >
                        <Input
                            ref={searchInputRef}
                            variant="outlined"
                            aria-label={searchAriaLabel ?? `${ariaLabel} search`}
                            aria-controls={listboxId}
                            aria-autocomplete="list"
                            aria-activedescendant={
                                activeIndex >= 0 ? optionId(activeIndex) : undefined
                            }
                            placeholder={searchPlaceholder}
                            value={filterQuery}
                            onChange={(event) => onFilterChange(event.target.value)}
                            onKeyDown={onKeyDown}
                            className="text-sm"
                        />
                    </div>
                ) : null}
                <div
                    className="max-h-60 overflow-y-auto py-1"
                    role="listbox"
                    id={listboxId}
                    aria-label={ariaLabel}
                >
                    {filteredOptions.map((option, index) => (
                        <SelectOptionRow
                            key={option.value}
                            active={index === activeIndex}
                            index={index}
                            onChoose={onChoose}
                            onPoint={setActiveIndex}
                            option={option}
                            optionId={optionId}
                            selected={option.value === value}
                        />
                    ))}
                </div>
                {filteredOptions.length === 0 ? (
                    <p className="px-3 py-2 text-sm font-normal text-neutral-500" role="status">
                        No matches.
                    </p>
                ) : null}
            </FieldControlContext.Provider>
        </div>
    );
}

export function Select({
    ariaLabel,
    options,
    value,
    onValueChange,
    placeholder = 'Select an option',
    disabled = false,
    className,
    searchable = false,
    searchPlaceholder = 'Search…',
    searchAriaLabel,
    onBlur,
}: SelectProps) {
    const [open, setOpen] = React.useState(false);
    const [filterQuery, setFilterQuery] = React.useState('');
    const [activeIndex, setActiveIndex] = React.useState(-1);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const searchInputRef = React.useRef<HTMLInputElement>(null);
    const openingActiveIndexRef = React.useRef<number | null>(null);
    const previousOpenRef = React.useRef(false);
    const previousFilterQueryRef = React.useRef(filterQuery);
    const listboxId = React.useId();
    const field = useFieldControl();
    const selectedOption = options.find((option) => option.value === value);
    const filteredOptions = React.useMemo(
        () =>
            searchable
                ? options.filter((option) => optionMatchesFilter(option, filterQuery))
                : options,
        [filterQuery, options, searchable],
    );
    const enabledIndexes = React.useMemo(
        () => filteredOptions.flatMap((option, index) => (option.disabled ? [] : [index])),
        [filteredOptions],
    );
    const optionId = React.useCallback(
        (index: number) => `${listboxId}-option-${index}`,
        [listboxId],
    );

    React.useEffect(() => {
        const wasOpen = previousOpenRef.current;
        const filterChanged = previousFilterQueryRef.current !== filterQuery;

        if (open && !wasOpen) {
            const selectedIndex = filteredOptions.findIndex(
                (option) => option.value === value && !option.disabled,
            );
            setActiveIndex(
                openingActiveIndexRef.current ??
                    (selectedIndex >= 0 ? selectedIndex : (enabledIndexes[0] ?? -1)),
            );
            openingActiveIndexRef.current = null;
        } else if (open && filterChanged) {
            setActiveIndex(enabledIndexes[0] ?? -1);
        }

        previousOpenRef.current = open;
        previousFilterQueryRef.current = filterQuery;
    }, [enabledIndexes, filterQuery, filteredOptions, open, value]);

    React.useEffect(() => {
        if (!open) setFilterQuery('');
    }, [open]);

    React.useEffect(() => {
        if (!open || !searchable) return;
        const id = requestAnimationFrame(() => searchInputRef.current?.focus());
        return () => cancelAnimationFrame(id);
    }, [open, searchable]);

    React.useEffect(() => {
        if (!open || activeIndex < 0) return;
        document.getElementById(optionId(activeIndex))?.scrollIntoView?.({ block: 'nearest' });
    }, [activeIndex, open, optionId]);

    React.useEffect(() => {
        if (!open) return;
        const handlePointerDown = (event: PointerEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [open]);

    const choose = (option: SelectOption) => {
        if (option.disabled) return;
        onValueChange(option.value);
        setOpen(false);
        triggerRef.current?.focus();
    };

    const handleListKeys = (event: SelectKeyEvent) =>
        handleSelectKey(event, {
            activeIndex,
            enabledIndexes,
            filteredOptions,
            filterQuery,
            open,
            trigger: triggerRef.current,
            value,
            choose,
            setActiveIndex,
            setOpen,
            setOpeningActiveIndex: (index) => {
                openingActiveIndexRef.current = index;
            },
        });

    return (
        <div
            ref={containerRef}
            className="relative"
            onKeyDown={(event) => {
                if (event.key === 'Escape' && open) {
                    event.preventDefault();
                    event.stopPropagation();
                    setOpen(false);
                    triggerRef.current?.focus();
                }
            }}
        >
            <SelectTrigger
                activeIndex={activeIndex}
                ariaLabel={ariaLabel}
                className={className}
                disabled={disabled}
                field={field}
                listboxId={listboxId}
                onBlur={onBlur}
                onKeyDown={handleListKeys}
                open={open}
                optionId={optionId}
                placeholder={placeholder}
                searchable={searchable}
                selectedOption={selectedOption}
                setOpen={setOpen}
                triggerRef={triggerRef}
            />
            {open ? (
                <SelectPopup
                    activeIndex={activeIndex}
                    ariaLabel={ariaLabel}
                    filterQuery={filterQuery}
                    filteredOptions={filteredOptions}
                    listboxId={listboxId}
                    onChoose={choose}
                    onFilterChange={setFilterQuery}
                    onKeyDown={handleListKeys}
                    optionId={optionId}
                    searchAriaLabel={searchAriaLabel}
                    searchInputRef={searchInputRef}
                    searchPlaceholder={searchPlaceholder}
                    searchable={searchable}
                    setActiveIndex={setActiveIndex}
                    value={value}
                />
            ) : null}
        </div>
    );
}
